import { useMemo, useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { UploadCloud, Loader2, X, Film, Image as ImageIcon, Plus, Check, AlertCircle, Link2, FolderOpen, RotateCcw, Trash2, Layers } from "lucide-react";
import { toast } from "sonner";
import { MentionInput } from "../components/MentionInput";
import { PeoplePicker } from "../components/PeoplePicker";

let _uid = 0;
const baseName = (name) => name.replace(/\.[^.]+$/, "");
const CONCURRENCY = 3; // upload this many files at once for big batches

const fmtEta = (s) => {
  if (s == null) return null;
  if (s < 5) return "finishing up…";
  if (s < 60) return `about ${s}s left`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m < 60) return `about ${m}m${rem ? ` ${rem}s` : ""} left`;
  const h = Math.floor(m / 60);
  return `about ${h}h ${m % 60}m left`;
};

// Recursively read a dropped folder (DataTransferItem entry) into a flat File list.
function readEntry(entry) {
  return new Promise((resolve) => {
    if (!entry) return resolve([]);
    if (entry.isFile) {
      entry.file((file) => resolve([file]), () => resolve([]));
    } else if (entry.isDirectory) {
      const reader = entry.createReader();
      const collected = [];
      const readBatch = () => {
        reader.readEntries(async (entries) => {
          if (!entries.length) {
            const nested = await Promise.all(collected.map((e) => readEntry(e)));
            resolve(nested.flat());
          } else {
            collected.push(...entries);
            readBatch();
          }
        }, () => resolve([]));
      };
      readBatch();
    } else resolve([]);
  });
}

export default function Upload() {
  const nav = useNavigate();
  const { user } = useAuth();
  const fileRef = useRef();
  const folderRef = useRef();
  const [items, setItems] = useState([]); // {id, file, preview, isVideo, title, status, progress, mediaId, error}
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState("");
  const [people, setPeople] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [asSet, setAsSet] = useState(false);
  const [albumTitle, setAlbumTitle] = useState("");
  const startRef = useRef(0);
  const [now, setNow] = useState(0);

  useEffect(() => {
    if (!uploading) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [uploading]);

  const addFiles = (fileList) => {
    const arr = Array.from(fileList || []);
    const valid = [];
    let skipped = 0;
    for (const f of arr) {
      if (!f.type.startsWith("image") && !f.type.startsWith("video")) { skipped += 1; continue; }
      const isVideo = f.type.startsWith("video");
      valid.push({
        id: ++_uid,
        file: f,
        preview: isVideo ? null : URL.createObjectURL(f),
        isVideo,
        title: baseName(f.name).slice(0, 120),
        status: "queued",
        progress: 0,
        mediaId: null,
        error: null,
      });
    }
    if (valid.length) {
      setItems((prev) => [...prev, ...valid]);
      toast.success(`Added ${valid.length} file${valid.length === 1 ? "" : "s"}${skipped ? ` · skipped ${skipped} non-media` : ""}`);
    } else if (skipped) {
      toast.error("Only images & videos are supported");
    }
  };

  const onDrop = async (e) => {
    e.preventDefault();
    const dtItems = e.dataTransfer?.items;
    if (dtItems && dtItems.length && dtItems[0].webkitGetAsEntry) {
      const entries = [];
      for (const it of Array.from(dtItems)) {
        const entry = it.webkitGetAsEntry?.();
        if (entry) entries.push(entry);
      }
      if (entries.length) {
        const toastId = toast.loading("Reading dropped folder…");
        const files = (await Promise.all(entries.map((en) => readEntry(en)))).flat();
        toast.dismiss(toastId);
        addFiles(files);
        return;
      }
    }
    addFiles(e.dataTransfer.files);
  };

  const removeItem = (id) => setItems((prev) => prev.filter((i) => i.id !== id));
  const setTitle = (id, title) => setItems((prev) => prev.map((i) => (i.id === id ? { ...i, title } : i)));
  const patch = (id, p) => setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...p } : i)));
  const clearDone = () => setItems((prev) => prev.filter((i) => i.status !== "done"));

  const tagPeople = async (mediaId) => {
    if (!people.length && !/@[a-zA-Z0-9_]{3,}/.test(`${description} ${albumTitle}`)) return;
    try { await api.put(`/media/${mediaId}/people`, { usernames: people.map((p) => p.username) }); }
    catch { toast.error("Posted, but tagging people failed — add them from Edit"); }
  };

  const putPart = (url, blob, onProgress) => new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.upload.onprogress = (e) => onProgress(e.loaded);
    xhr.onload = () => {
      const etag = xhr.getResponseHeader("ETag");
      if (xhr.status >= 200 && xhr.status < 300 && etag) resolve(etag);
      else reject(Object.assign(new Error("part failed"), { status: xhr.status, noEtag: !etag }));
    };
    xhr.onerror = () => reject(new Error("network"));
    xhr.send(blob);
  });

  const uploadDirect = async (item, meta) => {
    const total = item.file.size;
    const { data: init } = await api.post("/upload/direct/init", meta);
    const { upload_id: uploadId, part_size: size, urls } = init;
    const loaded = new Array(urls.length).fill(0);
    const parts = [];
    try {
      const jobs = urls.map((url, i) => async () => {
        const blob = item.file.slice(i * size, Math.min((i + 1) * size, total));
        for (let attempt = 1; ; attempt++) {
          try {
            const etag = await putPart(url, blob, (n) => {
              loaded[i] = n;
              const sent = loaded.reduce((a, b) => a + b, 0);
              patch(item.id, { progress: Math.max(1, Math.round((sent / total) * 98)) });
            });
            parts.push({ PartNumber: i + 1, ETag: etag });
            return;
          } catch (e) {
            if (e.noEtag || attempt >= 4) throw e;
            loaded[i] = 0;
            await new Promise((r) => setTimeout(r, 1000 * attempt));
          }
        }
      });
      await runPool(jobs, 4);
      const { data } = await api.post(`/upload/direct/complete/${uploadId}`, { parts }, { timeout: 0 });
      return data;
    } catch (e) {
      api.post(`/upload/direct/abort/${uploadId}`).catch(() => {});
      throw Object.assign(e, { directFailed: !e.response });
    }
  };

  const uploadChunked = async (item, meta) => {
    const CHUNK = 8 * 1024 * 1024;
    const total = item.file.size;
    const { data: init } = await api.post("/upload/init", meta);
    const uploadId = init.upload_id;
    let sent = 0;
    let index = 0;
    for (let start = 0; start < total; start += CHUNK) {
      const blob = item.file.slice(start, Math.min(start + CHUNK, total));
      let attempt = 0;
      while (true) {
        try {
          await api.post(`/upload/chunk/${uploadId}`, blob, {
            params: { index },
            headers: { "Content-Type": "application/octet-stream" },
            timeout: 0,
          });
          break;
        } catch (e) {
          attempt += 1;
          if (attempt >= 3) throw e;
          await new Promise((r) => setTimeout(r, 800 * attempt));
        }
      }
      sent += blob.size;
      index += 1;
      patch(item.id, { progress: Math.max(1, Math.round((sent / total) * 98)) });
    }
    const { data } = await api.post(`/upload/complete/${uploadId}`, {}, { timeout: 0 });
    return data;
  };

  const uploadOne = async (item) => {
    patch(item.id, { status: "uploading", progress: 0, error: null });
    const meta = {
      filename: item.file.name,
      size: item.file.size,
      title: item.title.trim() || baseName(item.file.name),
      description,
      tags,
      content_type: item.file.type || null,
    };
    try {
      let data;
      try {
        data = await uploadDirect(item, meta);
      } catch (e) {
        if (!e.directFailed && e.response) throw e;
        patch(item.id, { progress: 0 });
        data = await uploadChunked(item, meta);
      }
      patch(item.id, { status: "done", progress: 100, mediaId: data.id });
      await tagPeople(data.id);
      return true;
    } catch (err) {
      const status = err.response?.status;
      let msg = formatApiError(err.response?.data?.detail);
      if (status === 413) msg = "Too large";
      else if (!err.response) msg = "Connection dropped — retry";
      patch(item.id, { status: "error", error: msg || "Upload failed" });
      return false;
    }
  };

  // Run uploads with a bounded concurrency pool so big batches upload fast.
  const runPool = async (jobs, size) => {
    let i = 0;
    const worker = async () => {
      while (i < jobs.length) {
        const job = jobs[i++];
        await job();
      }
    };
    await Promise.all(Array.from({ length: Math.min(size, jobs.length) }, worker));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (asSet) return submitSet();
    const pending = items.filter((i) => i.status === "queued" || i.status === "error");
    if (pending.length === 0) return toast.error("Add at least one file");
    startRef.current = Date.now();
    setNow(Date.now());
    setUploading(true);
    let ok = 0;
    await runPool(pending.map((item) => async () => { if (await uploadOne(item)) ok += 1; }), CONCURRENCY);
    setUploading(false);
    setItems((prev) => {
      const done = prev.filter((i) => i.status === "done");
      if (done.length === 1) {
        toast.success("Uploaded! Tags generating…");
        setTimeout(() => nav(`/media/${done[0].mediaId}`), 600);
      } else if (done.length > 1) {
        toast.success(`${done.length} files uploaded! AI tags are generating…`);
        setTimeout(() => nav(`/u/${user.username}`), 900);
      }
      return prev;
    });
    if (ok === 0) toast.error("All uploads failed — check your connection and retry");
  };

  const submitSet = async () => {
    if (items.length < 2) return toast.error("A set needs at least 2 files");
    if (items.length > 30) return toast.error("A set can hold at most 30 files");
    startRef.current = Date.now();
    setNow(Date.now());
    setUploading(true);
    const fd = new FormData();
    items.forEach((it) => fd.append("files", it.file));
    fd.append("title", albumTitle.trim() || items[0].title.trim() || "Untitled set");
    fd.append("description", description);
    fd.append("tags", tags);
    try {
      const { data } = await api.post("/media/set", fd, { timeout: 0 });
      await tagPeople(data.id);
      toast.success("Set published! AI tags generating…");
      setTimeout(() => nav(`/media/${data.id}`), 700);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Set upload failed");
    } finally {
      setUploading(false);
    }
  };

  const stats = useMemo(() => {
    const total = items.length;
    const done = items.filter((i) => i.status === "done").length;
    const uploadingN = items.filter((i) => i.status === "uploading").length;
    const failed = items.filter((i) => i.status === "error").length;
    const queued = items.filter((i) => i.status === "queued").length;
    const overall = total ? Math.round(items.reduce((a, i) => a + (i.status === "done" ? 100 : i.progress), 0) / total) : 0;

    // Live ETA from measured throughput (bytes sent / elapsed).
    const batch = items.filter((i) => ["queued", "uploading", "done"].includes(i.status));
    const totalBytes = batch.reduce((a, i) => a + (i.file?.size || 0), 0);
    const sentBytes = batch.reduce((a, i) => a + (i.file?.size || 0) * (i.status === "done" ? 1 : i.progress / 100), 0);
    const elapsed = startRef.current ? (now - startRef.current) / 1000 : 0;
    let eta = null;
    if (uploading && elapsed > 1.5 && sentBytes > 0 && sentBytes < totalBytes) {
      const speed = sentBytes / elapsed; // bytes/sec
      if (speed > 0) eta = Math.round((totalBytes - sentBytes) / speed);
    }
    return { total, done, uploadingN, failed, queued, overall, eta };
  }, [items, now, uploading]);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-20 pb-16">
      <h1 className="font-heading text-4xl sm:text-5xl uppercase tracking-tighter text-white mb-4">Drop your <span className="text-[#00E5FF]">media</span></h1>
      <Link to="/import" data-testid="upload-goto-import" className="inline-flex items-center gap-2 mb-8 border border-[#00E5FF]/40 text-[#7fe9ff] hover:bg-[#00E5FF]/10 hover:border-[#00E5FF] transition-colors px-4 py-2 font-heading uppercase tracking-widest text-xs rounded-lg">
        <Link2 size={14} /> Or import from a URL instead
      </Link>

      <form onSubmit={submit} className="space-y-5">
        {stats.total === 0 ? (
          <div
            data-testid="upload-dropzone"
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            className="border-2 border-dashed border-[#1C1C1F] hover:border-[#00E5FF] transition-colors cursor-pointer py-20 flex flex-col items-center justify-center bg-[#0A0A0A] rounded-2xl"
          >
            <UploadCloud className="text-[#52525B] mb-4" size={48} />
            <p className="font-heading uppercase tracking-widest text-sm text-white">Click or drag files &amp; folders</p>
            <p className="font-mono text-[10px] text-[#52525B] mt-2">JPG · PNG · GIF · MP4 · WEBM</p>
            <button
              type="button"
              data-testid="upload-pick-folder"
              onClick={(e) => { e.stopPropagation(); folderRef.current?.click(); }}
              className="mt-5 inline-flex items-center gap-2 border border-[#1C1C1F] hover:border-[#00E5FF] text-[#A1A1AA] hover:text-white transition-colors px-4 py-2 rounded-lg font-heading uppercase tracking-widest text-[11px]"
            >
              <FolderOpen size={14} /> Select a folder
            </button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="upload-file-list">
            {/* batch summary + overall progress */}
            <div className="bg-[#0A0A0A] border border-[#1C1C1F] rounded-2xl p-4" data-testid="upload-batch-summary">
              <div className="flex items-center justify-between mb-2">
                <span className="font-heading uppercase tracking-widest text-xs text-white">{stats.total} in queue</span>
                <span className="flex items-center gap-3">
                  {stats.eta != null && (
                    <span data-testid="upload-eta" className="font-mono text-[11px] text-[#00E5FF]">{fmtEta(stats.eta)}</span>
                  )}
                  <span className="font-mono text-[11px] text-[#52525B]">{stats.overall}%</span>
                </span>
              </div>
              <div className="h-2 bg-[#141414] rounded-full overflow-hidden">
                <div className="h-full bg-[#00E5FF] rounded-full transition-all duration-300 shadow-[0_0_10px_#00E5FF]" style={{ width: `${stats.overall}%` }} />
              </div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 font-mono text-[11px]">
                <span className="text-[#3fb950]" data-testid="upload-stat-done">{stats.done} done</span>
                <span className="text-[#00E5FF]">{stats.uploadingN} uploading</span>
                <span className="text-[#A1A1AA]">{stats.queued} queued</span>
                {stats.failed > 0 && <span className="text-[#ff3b5c]" data-testid="upload-stat-failed">{stats.failed} failed</span>}
                <span className="ml-auto flex items-center gap-3">
                  {stats.done > 0 && (
                    <button type="button" data-testid="upload-clear-done" onClick={clearDone} className="text-[#52525B] hover:text-white transition-colors flex items-center gap-1"><Trash2 size={12} /> Clear done</button>
                  )}
                </span>
              </div>
            </div>

            {items.map((it) => (
              <div key={it.id} data-testid={`upload-item-${it.id}`} className="flex gap-3 border border-[#1C1C1F] bg-[#0A0A0A] p-3 rounded-xl">
                <div className="w-20 h-20 shrink-0 bg-black border border-[#1C1C1F] rounded-lg flex items-center justify-center overflow-hidden relative">
                  {it.preview ? (
                    <img src={it.preview} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Film className="text-[#52525B]" size={26} />
                  )}
                  <div className="absolute top-1 left-1 bg-black/70 text-white px-1 py-0.5 font-mono text-[8px] flex items-center gap-0.5">
                    {it.isVideo ? <Film size={8} /> : <ImageIcon size={8} />}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <input
                    data-testid={`upload-item-title-${it.id}`}
                    value={it.title}
                    onChange={(e) => setTitle(it.id, e.target.value)}
                    disabled={it.status === "uploading" || it.status === "done"}
                    placeholder="Title"
                    className="w-full bg-[#141414] border border-[#1C1C1F] text-white text-sm px-3 py-2 focus:outline-none focus:border-[#00E5FF] rounded-xl disabled:opacity-60"
                  />
                  <p className="font-mono text-[10px] text-[#52525B] mt-1 truncate">{it.file.name}</p>
                  {it.status === "uploading" && (
                    <div className="mt-2 h-1.5 bg-[#1C1C1F] rounded overflow-hidden">
                      <div className="h-full bg-[#00E5FF] transition-all duration-200" style={{ width: `${it.progress}%` }} />
                    </div>
                  )}
                  {it.status === "error" && <p className="text-[#ff3b5c] font-mono text-[10px] mt-1 flex items-center gap-1"><AlertCircle size={11} /> {it.error}</p>}
                </div>
                <div className="shrink-0 flex items-center">
                  {it.status === "done" ? (
                    <span className="text-[#3fb950] flex items-center gap-1 font-mono text-[10px]"><Check size={14} /> Done</span>
                  ) : it.status === "uploading" ? (
                    <span className="text-[#00E5FF] font-heading text-sm">{it.progress}%</span>
                  ) : (
                    <button type="button" data-testid={`upload-remove-${it.id}`} onClick={() => removeItem(it.id)} className="text-[#52525B] hover:text-[#ff3b5c] transition-colors p-1"><X size={16} /></button>
                  )}
                </div>
              </div>
            ))}

            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                data-testid="upload-add-more"
                onClick={() => fileRef.current?.click()}
                className="border border-dashed border-[#1C1C1F] hover:border-[#00E5FF] text-[#A1A1AA] hover:text-white transition-colors py-3 flex items-center justify-center gap-2 font-heading uppercase tracking-widest text-xs rounded-xl"
              >
                <Plus size={15} /> Add files
              </button>
              <button
                type="button"
                data-testid="upload-add-folder"
                onClick={() => folderRef.current?.click()}
                className="border border-dashed border-[#1C1C1F] hover:border-[#00E5FF] text-[#A1A1AA] hover:text-white transition-colors py-3 flex items-center justify-center gap-2 font-heading uppercase tracking-widest text-xs rounded-xl"
              >
                <FolderOpen size={15} /> Add folder
              </button>
            </div>
          </div>
        )}
        <input ref={fileRef} type="file" accept="image/*,video/*" multiple className="hidden" data-testid="file-input" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
        <input ref={folderRef} type="file" webkitdirectory="" directory="" multiple className="hidden" data-testid="folder-input" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />

        <div className="bg-[#0A0A0A] border border-[#1C1C1F] rounded-xl p-4">
          <button
            type="button"
            data-testid="upload-set-toggle"
            onClick={() => setAsSet((v) => !v)}
            className="flex items-center gap-3 w-full text-left"
          >
            <span className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${asSet ? "bg-[#00E5FF]" : "bg-[#1C1C1F]"}`}>
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${asSet ? "translate-x-5" : ""}`} />
            </span>
            <span>
              <span className="flex items-center gap-2 text-white font-heading uppercase tracking-widest text-xs"><Layers size={14} className="text-[#00E5FF]" /> Combine into one post (set)</span>
              <span className="block text-[#52525B] font-mono text-[10px] mt-1">All files become a single swipeable album instead of separate posts.</span>
            </span>
          </button>
          {asSet && (
            <input
              data-testid="upload-album-title"
              value={albumTitle}
              onChange={(e) => setAlbumTitle(e.target.value)}
              placeholder="Set title (AI names it if blank)"
              className="w-full bg-[#141414] border border-[#1C1C1F] text-white text-sm px-3 py-2.5 mt-3 focus:outline-none focus:border-[#00E5FF] rounded-xl"
            />
          )}
        </div>

        <MentionInput as="textarea" data-testid="upload-description" value={description} onChange={setDescription} placeholder="Description" rows={2} className="w-full bg-[#141414] border border-[#1C1C1F] text-white px-4 py-3 focus:outline-none focus:border-[#00E5FF] transition-colors placeholder:text-[#52525B] rounded-xl resize-none" />
        <PeoplePicker value={people} onChange={setPeople} testid="upload-people" />
        <p className="text-[#52525B] font-mono text-[10px]">Videos up to 2.5 hours.</p>

        {stats.total > 0 && (
          <button data-testid="upload-submit" disabled={uploading} className="w-full bg-[#00E5FF] text-black font-heading uppercase tracking-[0.2em] text-sm py-4 rounded-xl hover:bg-[#00c9e0] transition-colors disabled:opacity-50 flex items-center justify-center gap-2 shadow-[0_0_18px_rgba(0,229,255,0.35)]">
            {uploading ? <><Loader2 className="animate-spin" size={18} /> {asSet ? "Publishing set…" : `Uploading ${stats.done}/${stats.total}…`}</> : asSet ? <><Layers size={16} /> Publish set of {stats.total}</> : stats.failed > 0 ? <><RotateCcw size={16} /> Publish / retry {stats.queued + stats.failed} file{stats.queued + stats.failed === 1 ? "" : "s"}</> : `Publish ${stats.total} file${stats.total === 1 ? "" : "s"}`}
          </button>
        )}
      </form>
    </div>
  );
}
